import React from "react";
import { Modal, Box } from "@mui/joy";
import type { Dispatch, SetStateAction } from "react";
import type { Dive } from "../../../api/followup/Followup.typed";
import type { Training } from "../../../api/training/Training.typed";
import type { ZekayCustomer } from "../../../api/customers/Customer.typed";
import { useCustomer } from "../../../contexts/api";
import { generateModuleSummary } from "./helpers/followupSummaryHelpers";
import type { CriteriaSummary } from "./helpers/followupSummaryHelpers";
import SummaryLoadingView from "./display/FollowUpSummaryModal/SummaryLoadingView";
import SummaryHeader from "./display/FollowUpSummaryModal/SummaryHeader";
import SummaryModuleBlock from "./display/FollowUpSummaryModal/SummaryModuleBlock";
import SummaryActions from "./display/FollowUpSummaryModal/SummaryActions";
import { colorTokens, textTokens } from "../../../styles/globalStyles";

type RefreshFunction<T> = () => Promise<T>;
type RefreshFunctionWithId<T> = (id?: string) => Promise<T>;
type RefreshTrainingFunction =
  | RefreshFunction<Training[] | undefined>
  | RefreshFunctionWithId<Training[] | undefined>;

type FollowUpSummaryModalProps = {
  open: boolean;
  onClose: () => void;
  customerId: number | undefined;
  templateId: number | undefined;
  customer?: ZekayCustomer;
  setTrainings?: Dispatch<SetStateAction<Training[] | undefined>>;
  refreshTrainings?: RefreshTrainingFunction;
};

export default function FollowUpSummaryModal(props: FollowUpSummaryModalProps) {
  const { getCustomer } = useCustomer();
  const [training, setTraining] = React.useState<Training>();
  const [dives, setDives] = React.useState<Dive[]>([]);
  const [moduleSummaries, setModuleSummaries] = React.useState<Record<string, CriteriaSummary[]>>(
    {},
  );
  const [customer, setCustomer] = React.useState<ZekayCustomer>();
  const isSmallScreen = window.innerWidth <= 600;
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    if (!props.customerId || !props.templateId) return;
    setIsLoading(true);

    const applyCustomerData = (customerData: ZekayCustomer | undefined) => {
      setCustomer(customerData);
      const selectedTraining = customerData?.Trainings?.find(
        (t) => t.TrainingTemplateID === props.templateId,
      );
      setTraining(selectedTraining);
      if (selectedTraining) {
        setDives(selectedTraining.Dives || []);
        generateModuleSummary(selectedTraining).then((summaries) => {
          if (summaries) setModuleSummaries(summaries);
        });
      }
    };

    if (props.customer?.Id === props.customerId) {
      applyCustomerData(props.customer);
      setIsLoading(false);
      return;
    }

    getCustomer(String(props.customerId))
      .then(applyCustomerData)
      .finally(() => setIsLoading(false));
  }, [props.open, props.customerId, props.templateId, props.customer, getCustomer]);

  if (isLoading) {
    return <SummaryLoadingView open={props.open} onClose={props.onClose} />;
  }

  const customerName = [customer?.FirstName, customer?.LastName].filter(Boolean).join(" ");
  const trainingTitle = training?.Qualification ?? "";

  return (
    <Modal
      open={props.open}
      onClose={props.onClose}
      disableScrollLock
      keepMounted={false}
      slotProps={{ backdrop: { sx: { opacity: 0.1 } } }}
    >
      <>
        <Box
          sx={{
            position: "fixed",
            top: 10,
            left: 10,
            right: 10,
            height: "calc(100% - 30px)",
            overflow: "auto",
            borderRadius: "10px",
            padding: "clamp(0.5rem, 2vw, 1rem)",
            backgroundColor: colorTokens.bg.sheetLight,
            color: textTokens.color.inverse,
            "& .MuiTypography-root": { color: textTokens.color.inverse },
            "& .MuiInputBase-input, & textarea, & input": { color: textTokens.color.inverse },
            "& .MuiCard-root, & .MuiSheet-root, & .MuiAccordion-root, & .MuiAccordionDetails-root, & .MuiAccordionSummary-root":
              {
                backgroundColor: colorTokens.bg.sheetLight,
                color: textTokens.color.inverse,
              },
            margin: "0 auto",
          }}
        >
          <Box sx={{ width: "100%" }}>
            <SummaryHeader
              customerName={customerName}
              trainingTitle={trainingTitle}
              dives={dives}
              isSmallScreen={isSmallScreen}
            />

            {Object.keys(moduleSummaries).map((moduleName, index) => (
              <SummaryModuleBlock
                key={index}
                moduleName={moduleName}
                criteria={moduleSummaries[moduleName]}
              />
            ))}
          </Box>
        </Box>

        <SummaryActions onClose={props.onClose} />
      </>
    </Modal>
  );
}
